
import * as Sentry from '@sentry/nextjs';
import { AppProps } from 'next/app';
import Script from "next/script";

import { SessionProvider } from "next-auth/react"; // ✅ added

import AuthProvider from '~/components/Auth/AuthProvider';
import ErrorFallback from '~/components/Common/ErrorFallback';
import GlobalDataProvider from '~/components/Common/GlobalDataProvider';

import { Provider } from 'react-redux';
import { SubscriptionProvider } from '~/components/Subscription/SubscriptionProvider';
import { store } from '~/redux/store';
import '../styles/components/calender.css';
import '../styles/globals.css';

import { useRouter } from "next/router";
import { useEffect, useState } from 'react';
import { Meta } from '~/components/Common/Meta';
import { Toaster } from '~/components/ui/toaster';
import dayjs from '~/utils/dayjs-config';
import { localStore, StoreKey } from '~/utils/local-store';
const rtlLanguages = ['ar']

// ✅ destructure session here
const MyApp = ({ Component, pageProps: { session, ...pageProps } }: AppProps) => {
    const router = useRouter()
    const [language, setLanguage] = useState('en')

    useEffect(() => {

        const storedLanguage = localStorage.getItem('language') || 'en'
        setLanguage(storedLanguage)

        const isRTL = rtlLanguages.includes(storedLanguage)
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
        dayjs.locale(storedLanguage)
    }, [language])

    useEffect(() => {
        if (typeof window !== "undefined") {
            const originalRemoveChild = Node.prototype.removeChild;

            Node.prototype.removeChild = function <T extends Node>(child: T): T {
                try {
                    return originalRemoveChild.call(this, child) as T;
                } catch (e) {
                    console.warn("Prevented removeChild error:", e);
                    return child; // must return T
                }
            };
        }
    }, []);
    useEffect(() => {
        const handleRouteError = (err: any, _url: string) => {
            const isChunkError =
                err?.message?.includes("Failed to load script") ||
                err?.message?.includes("Loading chunk") ||
                err?.name === "ChunkLoadError";

            if (isChunkError) {
                console.warn("Chunk load failed — new build likely deployed. Prompting user.");
                // Notify the NewBuildBanner to show instead of auto-reloading
                window.dispatchEvent(new CustomEvent("new-build-available"));
            }
        };

        router.events.on("routeChangeError", handleRouteError);

        return () => {
            router.events.off("routeChangeError", handleRouteError);
        };
    }, [router]);

    useEffect(() => {
        const handleRouteChange = (url: string) => {
            const pathname = url.split('?')[0];
            // Don't remove filter if we're on the product page or product edit page
            if (pathname !== '/business/product' && pathname !== '/business/product/edit') {
                localStore.remove(StoreKey.PRODUCT_FILTER);
            }
        };

        router.events.on("routeChangeStart", handleRouteChange);

        return () => {
            router.events.off("routeChangeStart", handleRouteChange);
        };
    }, [router]);
    useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
        const message = event.message || "";

        if (
        message.includes("Loading chunk") ||
        message.includes("ChunkLoadError") ||
        message.includes("Failed to fetch dynamically imported module")
        ) {
        window.dispatchEvent(new Event("new-build-available"));
        }
    };

    window.addEventListener("error", handleChunkError);

    return () => {
        window.removeEventListener("error", handleChunkError);
    };
    }, []);
    // useInterval(
    //     async () => {
    //         const { buildId } = await axios.get('/api/build-id').then((res) => res.data)
    //
    //         if (buildId && process.env.BUILD_ID && buildId !== process.env.BUILD_ID) {
    //             window.location.reload()
    //         }
    //     },
    //     { interval: 5000 * 60 }
    // )
    const isInvoicePreview = pageProps?.skipGlobalMeta || router.pathname === '/invoice/[id]'

    return (
        <>
            {!isInvoicePreview ? (
                <Meta>
                    <meta name="application-name" content="simplystock.co" />
                    <meta name="apple-mobile-web-app-capable" content="yes" />
                    <meta name="apple-mobile-web-app-status-bar-style" content="default" />
                    <meta name="apple-mobile-web-app-title" content="Simply" />

                    <meta name="format-detection" content="telephone=no" />
                    <meta name="mobile-web-app-capable" content="yes" />
                    <meta name="msapplication-TileColor" content="#0051E0" />
                    <meta name="msapplication-tap-highlight" content="no" />
                    <meta name="theme-color" content="#000000" />

                    <link rel="apple-touch-icon" href="/icons/apple-touch-icon.png" />

                    <link rel="icon" type="image/png" sizes="32x32" href="/icons/favicon-32x32.png" />
                    <link rel="icon" type="image/png" sizes="16x16" href="/icons/favicon-16x16.png" />
                    <link rel="manifest" href="/manifest.json" />
                    <link rel="mask-icon" href="/icons/apple-touch-icon.png" color="#0051E0" />
                    <link rel="shortcut icon" href="/favicon.ico" />

                    <meta name="twitter:card" content="summary" />
                    <meta name="twitter:url" content="https://web.simplystock.co" />
                    <meta name="twitter:title" content="Simply" />

                    <meta
                        name="twitter:image"
                        content="https://simplyazurestorage.blob.core.windows.net/common/Download%20Options.png?sv=2024-11-04&st=2024-10-17T10%3A24%3A02Z&se=2034-10-15T10%3A24%3A02Z&sr=b&sp=r&sig=9AxfuakmxcPZlIkXS699OwyI4fwe12JWXoZ90ruv2m4%3D"
                    />

                    <meta property="og:type" content="website" />

                    <meta property="og:site_name" content="Simply" />
                    <meta property="og:url" content="https://web.simplystock.co" />

                    <meta
                        property="og:image"
                        content="https://simplyazurestorage.blob.core.windows.net/common/Download%20Options.png?sv=2024-11-04&st=2024-10-17T10%3A24%3A02Z&se=2034-10-15T10%3A24%3A02Z&sr=b&sp=r&sig=9AxfuakmxcPZlIkXS699OwyI4fwe12JWXoZ90ruv2m4%3D"
                    />
                </Meta>
            ) : null}

            {/* <Script src="https://js.durianpay.id/0.1.39/durianpay.min.js"></Script> */}
            <Provider store={store}>
                {/* ✅ Wrap everything inside SessionProvider */}
                <SessionProvider session={session}>
                    <Toaster />
                    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                        <AuthProvider>
                            <SubscriptionProvider>
                                <GlobalDataProvider>
                                    <Component {...pageProps} />
                                </GlobalDataProvider>
                            </SubscriptionProvider>
                        </AuthProvider>
                    </Sentry.ErrorBoundary>
                </SessionProvider>
            </Provider>

            <Script
                src="https://cdn.paddle.com/paddle/v2/paddle.js"
                strategy="afterInteractive"
                onLoad={() => {
                    console.log("Paddle script loaded Paddle script loaded Paddle script loaded Paddle script loaded")
                }}
            />
        </>
    )
}

export default MyApp
